import { YearbookElement, PageGuide } from '@/types/yearbook';

export interface GuideLine {
  orientation: 'horizontal' | 'vertical';
  position: number; // x for vertical, y for horizontal
  type?: 'alignment' | 'spacing'; // spacing = 14.17pt gap guide
  from?: number; // start of the gap (ref edge)
  to?: number;   // end of the gap (dragged edge)
  crossStart?: number; // perpendicular axis start for the dimension line
  crossEnd?: number;   // perpendicular axis end for the dimension line
}

export interface SnapResult {
  x: number;
  y: number;
  guides: GuideLine[];
}

const SNAP_THRESHOLD = 6; // pixels tolerance
const SPACING_GAP = 14.1732; // 5mm in points — standard spacing guide

interface Edges {
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
  centerY: number;
}

function getEdges(x: number, y: number, w: number, h: number): Edges {
  return {
    left: x,
    right: x + w,
    top: y,
    bottom: y + h,
    centerX: x + w / 2,
    centerY: y + h / 2,
  };
}

/**
 * Compute snapped position and active guide lines for a dragged element.
 */
export function computeSnap(
  dragX: number,
  dragY: number,
  dragW: number,
  dragH: number,
  siblings: YearbookElement[],
  pageWidth: number,
  pageHeight: number,
  pageGuides: PageGuide[] = []
): SnapResult {
  const dragged = getEdges(dragX, dragY, dragW, dragH);

  // Collect all reference points (vertical = x values, horizontal = y values)
  const vRefs: number[] = [0, pageWidth / 2, pageWidth]; // page left, center, right
  const hRefs: number[] = [0, pageHeight / 2, pageHeight]; // page top, center, bottom

  for (const el of siblings) {
    const e = getEdges(el.position.x, el.position.y, el.size.width, el.size.height);
    vRefs.push(e.left, e.right, e.centerX);
    hRefs.push(e.top, e.bottom, e.centerY);
  }

  // User-defined page guides
  for (const g of pageGuides) {
    if (g.orientation === 'vertical') vRefs.push(g.position);
    else hRefs.push(g.position);
  }

  // For the dragged element, check its left, right, center against all vRefs
  const dragVPoints = [dragged.left, dragged.centerX, dragged.right];
  const dragHPoints = [dragged.top, dragged.centerY, dragged.bottom];

  let bestDx = Infinity;
  let snapX = dragX;
  const activeVGuides: number[] = [];

  for (const ref of vRefs) {
    for (let i = 0; i < dragVPoints.length; i++) {
      const diff = ref - dragVPoints[i];
      const absDiff = Math.abs(diff);
      if (absDiff < Math.abs(bestDx)) {
        bestDx = diff;
        snapX = dragX + diff;
        activeVGuides.length = 0;
        activeVGuides.push(ref);
      } else if (absDiff === Math.abs(bestDx) && absDiff <= SNAP_THRESHOLD) {
        activeVGuides.push(ref);
      }
    }
  }

  let bestDy = Infinity;
  let snapY = dragY;
  const activeHGuides: number[] = [];

  for (const ref of hRefs) {
    for (let i = 0; i < dragHPoints.length; i++) {
      const diff = ref - dragHPoints[i];
      const absDiff = Math.abs(diff);
      if (absDiff < Math.abs(bestDy)) {
        bestDy = diff;
        snapY = dragY + diff;
        activeHGuides.length = 0;
        activeHGuides.push(ref);
      } else if (absDiff === Math.abs(bestDy) && absDiff <= SNAP_THRESHOLD) {
        activeHGuides.push(ref);
      }
    }
  }

  // Only snap if within threshold
  if (Math.abs(bestDx) > SNAP_THRESHOLD) {
    snapX = dragX;
    activeVGuides.length = 0;
  }
  if (Math.abs(bestDy) > SNAP_THRESHOLD) {
    snapY = dragY;
    activeHGuides.length = 0;
  }

  // --- Spacing guides: snap when element is SPACING_GAP away from sibling edges or page edges ---
  const spacingGuides: GuideLine[] = [];
  const draggedSnapped = getEdges(snapX, snapY, dragW, dragH);

  // Collect all vertical (x) and horizontal (y) reference edges for spacing
  const vEdgePairs: { ref: number; guidePos: number; orientation: 'vertical' }[] = [];
  const hEdgePairs: { ref: number; guidePos: number; orientation: 'horizontal' }[] = [];

  // Page edges
  vEdgePairs.push({ ref: 0, guidePos: 0, orientation: 'vertical' });
  vEdgePairs.push({ ref: pageWidth, guidePos: pageWidth, orientation: 'vertical' });
  hEdgePairs.push({ ref: 0, guidePos: 0, orientation: 'horizontal' });
  hEdgePairs.push({ ref: pageHeight, guidePos: pageHeight, orientation: 'horizontal' });

  // Sibling edges
  for (const el of siblings) {
    const e = getEdges(el.position.x, el.position.y, el.size.width, el.size.height);
    vEdgePairs.push({ ref: e.left, guidePos: e.left, orientation: 'vertical' });
    vEdgePairs.push({ ref: e.right, guidePos: e.right, orientation: 'vertical' });
    hEdgePairs.push({ ref: e.top, guidePos: e.top, orientation: 'horizontal' });
    hEdgePairs.push({ ref: e.bottom, guidePos: e.bottom, orientation: 'horizontal' });
  }

  // Check vertical spacing (dragged left edge vs refs on left, dragged right edge vs refs on right)
  for (const vp of vEdgePairs) {
    // dragged left edge is SPACING_GAP to the right of ref
    const diffLeft = draggedSnapped.left - vp.ref;
    if (Math.abs(diffLeft - SPACING_GAP) <= SNAP_THRESHOLD) {
      const newX = vp.ref + SPACING_GAP;
      if (Math.abs(newX - snapX) <= SNAP_THRESHOLD) {
        snapX = newX;
        spacingGuides.push({ orientation: 'vertical', position: vp.guidePos, type: 'spacing', from: vp.ref, to: newX, crossStart: draggedSnapped.top, crossEnd: draggedSnapped.bottom });
      }
    }
    // dragged right edge is SPACING_GAP to the left of ref
    const diffRight = vp.ref - draggedSnapped.right;
    if (Math.abs(diffRight - SPACING_GAP) <= SNAP_THRESHOLD) {
      const newX = vp.ref - SPACING_GAP - dragW;
      if (Math.abs(newX - snapX) <= SNAP_THRESHOLD) {
        snapX = newX;
        const drRight = newX + dragW;
        spacingGuides.push({ orientation: 'vertical', position: vp.guidePos, type: 'spacing', from: drRight, to: vp.ref, crossStart: draggedSnapped.top, crossEnd: draggedSnapped.bottom });
      }
    }
  }

  // Check horizontal spacing
  for (const hp of hEdgePairs) {
    const diffTop = draggedSnapped.top - hp.ref;
    if (Math.abs(diffTop - SPACING_GAP) <= SNAP_THRESHOLD) {
      const newY = hp.ref + SPACING_GAP;
      if (Math.abs(newY - snapY) <= SNAP_THRESHOLD) {
        snapY = newY;
        spacingGuides.push({ orientation: 'horizontal', position: hp.guidePos, type: 'spacing', from: hp.ref, to: newY, crossStart: draggedSnapped.left, crossEnd: draggedSnapped.right });
      }
    }
    const diffBottom = hp.ref - draggedSnapped.bottom;
    if (Math.abs(diffBottom - SPACING_GAP) <= SNAP_THRESHOLD) {
      const newY = hp.ref - SPACING_GAP - dragH;
      if (Math.abs(newY - snapY) <= SNAP_THRESHOLD) {
        snapY = newY;
        const drBottom = newY + dragH;
        spacingGuides.push({ orientation: 'horizontal', position: hp.guidePos, type: 'spacing', from: drBottom, to: hp.ref, crossStart: draggedSnapped.left, crossEnd: draggedSnapped.right });
      }
    }
  }

  const guides: GuideLine[] = [
    ...Array.from(new Set(activeVGuides)).map((pos) => ({ orientation: 'vertical' as const, position: pos, type: 'alignment' as const })),
    ...Array.from(new Set(activeHGuides)).map((pos) => ({ orientation: 'horizontal' as const, position: pos, type: 'alignment' as const })),
    ...spacingGuides,
  ];

  return { x: Math.round(snapX), y: Math.round(snapY), guides };
}

export interface ResizeSnapResult {
  width: number;
  height: number;
  x: number;
  y: number;
  guides: GuideLine[];
}

/**
 * Compute snapped size (and position for top/left corners) during resize.
 * `corner` uses the same codes as resize handles: tl, tr, bl, br.
 */
export function computeResizeSnap(
  x: number,
  y: number,
  w: number,
  h: number,
  corner: string,
  siblings: YearbookElement[],
  pageWidth: number,
  pageHeight: number,
  pageGuides: PageGuide[] = []
): ResizeSnapResult {
  const edges = getEdges(x, y, w, h);

  const vRefs: number[] = [0, pageWidth / 2, pageWidth];
  const hRefs: number[] = [0, pageHeight / 2, pageHeight];

  for (const el of siblings) {
    const e = getEdges(el.position.x, el.position.y, el.size.width, el.size.height);
    vRefs.push(e.left, e.right, e.centerX);
    hRefs.push(e.top, e.bottom, e.centerY);
  }

  for (const g of pageGuides) {
    if (g.orientation === 'vertical') vRefs.push(g.position);
    else hRefs.push(g.position);
  }

  const guides: GuideLine[] = [];
  let snappedX = x, snappedY = y, snappedW = w, snappedH = h;

  // Snap the moving vertical edge (left or right depending on corner)
  const movingRight = corner.includes('r');
  const movingBottom = corner.includes('b');
  const movingLeft = corner.includes('l');
  const movingTop = corner.includes('t');

  // Vertical snap (x-axis edges)
  {
    // Only check the moving edges — never snap center during resize (it would move the anchored corner)
    const checkPoints: { value: number; type: 'edge' }[] = [];
    if (movingRight) checkPoints.push({ value: edges.right, type: 'edge' });
    if (movingLeft) checkPoints.push({ value: edges.left, type: 'edge' });

    let bestDiff = Infinity;
    let bestRef = 0;

    for (const ref of vRefs) {
      for (const cp of checkPoints) {
        const diff = Math.abs(ref - cp.value);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestRef = ref;
        }
      }
    }

    if (bestDiff <= SNAP_THRESHOLD) {
      guides.push({ orientation: 'vertical', position: bestRef });
      if (movingRight) {
        snappedW = bestRef - snappedX;
      } else if (movingLeft) {
        const oldRight = x + w;
        snappedX = bestRef;
        snappedW = oldRight - bestRef;
      }
    }
  }

  // Horizontal snap (y-axis edges)
  {
    const checkPoints: { value: number }[] = [];
    if (movingBottom) checkPoints.push({ value: edges.bottom });
    if (movingTop) checkPoints.push({ value: edges.top });

    let bestDiff = Infinity;
    let bestRef = 0;

    for (const ref of hRefs) {
      for (const cp of checkPoints) {
        const diff = Math.abs(ref - cp.value);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestRef = ref;
        }
      }
    }

    if (bestDiff <= SNAP_THRESHOLD) {
      guides.push({ orientation: 'horizontal', position: bestRef });
      if (movingBottom) {
        snappedH = bestRef - snappedY;
      } else if (movingTop) {
        const oldBottom = y + h;
        snappedY = bestRef;
        snappedH = oldBottom - bestRef;
      }
    }
  }

  return {
    x: Math.round(snappedX),
    y: Math.round(snappedY),
    width: Math.round(snappedW),
    height: Math.round(snappedH),
    guides,
  };
}
